import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { DialogCloseButton } from "../DialogCloseButton";
import {
  SINGLE_SHORTCUTS,
  RANGE_SHORTCUTS,
  matchSingleShortcut,
  matchRangeShortcut,
  formatSingle,
  formatRange,
  type SingleShortcut,
  type RangeShortcut,
} from "./shortcuts";
import type { DatePickerProps, DateRangeValue } from "./types";

const TRIGGER_SX = {
  minHeight: 40,
  justifyContent: "flex-start",
  textTransform: "none" as const,
  fontWeight: 400,
};

export const DatePicker = (props: DatePickerProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(props.defaultOpen ?? false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    props.onClose?.();
  };

  const triggerLabel =
    props.mode === "single"
      ? formatSingle(props.value, props.placeholder ?? "Pick a date")
      : formatRange(props.value);

  const tooltipText =
    props.mode === "single"
      ? (props.value ?? "No date selected")
      : !props.value.from && !props.value.to
        ? "No range selected"
        : `${props.value.from ?? "…"} → ${props.value.to ?? "…"}`;

  const body =
    props.mode === "single" ? (
      <SingleBody
        value={props.value}
        onChange={(v) => {
          props.onChange(v);
          handleClose();
        }}
        required={props.required}
        label={props.label ?? "Date"}
        isMobile={isMobile}
      />
    ) : (
      <RangeBody
        value={props.value}
        onChange={(v) => {
          props.onChange(v);
          handleClose();
        }}
      />
    );

  return (
    <>
      <Tooltip title={tooltipText}>
        <Button
          ref={setAnchorEl}
          variant="outlined"
          size="small"
          color="inherit"
          startIcon={<CalendarMonthIcon fontSize="small" />}
          onClick={handleOpen}
          fullWidth={props.fullWidth}
          sx={TRIGGER_SX}
          aria-label={
            props.label ? `${props.label}: ${triggerLabel}` : triggerLabel
          }
        >
          <Box
            component="span"
            sx={{
              flexGrow: 1,
              textAlign: "left",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {props.label &&
            props.mode === "range" &&
            !props.value.from &&
            !props.value.to
              ? `${props.label}: ${triggerLabel}`
              : triggerLabel}
          </Box>
        </Button>
      </Tooltip>

      {isMobile ? (
        <Dialog
          open={open}
          onClose={handleClose}
          fullScreen
          keepMounted={false}
        >
          <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
            {props.label ?? (props.mode === "range" ? "Date range" : "Date")}
            <DialogCloseButton
              onClick={handleClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            />
          </DialogTitle>
          <DialogContent dividers>{body}</DialogContent>
        </Dialog>
      ) : (
        <Popover
          open={open && anchorEl !== null}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{ paper: { sx: { maxWidth: 520 } } }}
        >
          <Box sx={{ p: 2 }}>{body}</Box>
        </Popover>
      )}
    </>
  );
};

type SingleBodyProps = {
  value: string | null;
  onChange: (next: string | null) => void;
  required?: boolean;
  label: string;
  isMobile: boolean;
};

const SingleBody = ({
  value,
  onChange,
  required,
  label,
  isMobile,
}: SingleBodyProps) => {
  const [draft, setDraft] = useState(value ?? "");
  const active = matchSingleShortcut(value);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      divider={<Divider orientation="vertical" flexItem />}
    >
      <ShortcutColumn<SingleShortcut>
        title="Quick select"
        shortcuts={SINGLE_SHORTCUTS}
        activeId={active?.id}
        onPick={(s) => onChange(s.compute())}
        trailing={
          !required ? (
            <Button
              size="medium"
              sx={{ justifyContent: "flex-start", py: 1 }}
              onClick={() => onChange(null)}
            >
              Clear
            </Button>
          ) : undefined
        }
      />
      <Box
        sx={{ minWidth: 220, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <TextField
          type="date"
          size="small"
          label={label}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
          // On mobile the quick-select column (Today/Yesterday/Tomorrow) stacks
          // above this field; auto-focusing the date input would scroll past it
          // (and can pop the OS date UI), forcing the user to deal with the date
          // field before they can reach "Today". Only auto-focus on desktop,
          // where the column sits alongside and stays visible.
          autoFocus={!isMobile}
        />
        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
          <Button
            variant="contained"
            size="small"
            disabled={!draft && required}
            onClick={() => onChange(draft || null)}
          >
            Apply
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

type RangeBodyProps = {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
};

const RangeBody = ({ value, onChange }: RangeBodyProps) => {
  // When a period is active, the resolved `from`/`to` are derived — don't
  // pre-fill the custom inputs with them, so the user can't accidentally
  // collapse the rolling period into a frozen date range by clicking Apply.
  const [fromDraft, setFromDraft] = useState(
    value.period ? "" : (value.from ?? ""),
  );
  const [toDraft, setToDraft] = useState(value.period ? "" : (value.to ?? ""));
  const active = matchRangeShortcut(value);

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      divider={<Divider orientation="vertical" flexItem />}
    >
      <ShortcutColumn<RangeShortcut>
        title="Quick select"
        shortcuts={RANGE_SHORTCUTS}
        activeId={active?.id}
        onPick={(s) => onChange(s.compute())}
      />
      <Box
        sx={{ minWidth: 240, display: "flex", flexDirection: "column", gap: 2 }}
      >
        <TextField
          type="date"
          size="small"
          label="From"
          value={fromDraft}
          onChange={(e) => setFromDraft(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
        <TextField
          type="date"
          size="small"
          label="To"
          value={toDraft}
          onChange={(e) => setToDraft(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
        />
        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
          <Button
            size="small"
            onClick={() => {
              setFromDraft("");
              setToDraft("");
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() =>
              onChange({ from: fromDraft || null, to: toDraft || null })
            }
          >
            Apply
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

type ShortcutColumnProps<T extends { id: string; label: string }> = {
  title: string;
  shortcuts: T[];
  activeId: string | undefined;
  onPick: (s: T) => void;
  trailing?: React.ReactNode;
};

const ShortcutColumn = <T extends { id: string; label: string }>({
  title,
  shortcuts,
  activeId,
  onPick,
  trailing,
}: ShortcutColumnProps<T>) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: 0.5,
      minWidth: 160,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
      {title}
    </Typography>
    {shortcuts.map((s) => (
      <Button
        key={s.id}
        size="medium"
        variant={s.id === activeId ? "contained" : "text"}
        sx={{ justifyContent: "flex-start", py: 1 }}
        onClick={() => onPick(s)}
      >
        {s.label}
      </Button>
    ))}
    {trailing}
  </Box>
);
